import { useNavigate, Link } from 'react-router-dom';
import { Plus, Shield, AlertTriangle, MoreHorizontal } from 'lucide-react';
import { useInsuranceTypeList } from '@/hooks/useInsuranceTypeList';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { ITypeListEntry, ITypeListGroup } from '@/@types/insuranceBuilder';

function TypeRow({ type }: { type: ITypeListEntry }) {
  const navigate = useNavigate();

  return (
    <li className="flex items-center gap-4 px-6 py-3 hover:bg-muted/20 transition-colors">
      <Link
        to={`/insurance/types/${type._id}`}
        className="flex-1 min-w-0 text-sm font-medium text-foreground hover:underline truncate"
      >
        {type.name}
      </Link>
      <span className="shrink-0 text-sm text-muted-foreground w-28 text-right">
        {type.fieldCount} field{type.fieldCount !== 1 ? 's' : ''}
        {' · '}
        {type.policyCount} polic{type.policyCount !== 1 ? 'ies' : 'y'}
      </span>
      <Button
        variant="outline"
        size="sm"
        className="shrink-0"
        onClick={() => navigate(`/insurance/types/${type._id}`)}
      >
        Edit
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="shrink-0 px-2">
            <MoreHorizontal className="w-4 h-4" aria-hidden="true" />
            <span className="sr-only">More actions for {type.name}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem disabled>Duplicate</DropdownMenuItem>
          <DropdownMenuItem disabled className="text-destructive">
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}

function GroupItem({ group }: { group: ITypeListGroup }) {
  return (
    <AccordionItem value={group.providerName} className="border border-border rounded-md mb-3">
      <AccordionTrigger className="px-5 py-3 hover:no-underline">
        <span className="flex items-center gap-3">
          <span className="text-sm font-semibold text-foreground">{group.providerName}</span>
          <Badge variant="secondary" className="text-xs">
            {group.types.length} type{group.types.length !== 1 ? 's' : ''}
          </Badge>
        </span>
      </AccordionTrigger>
      <AccordionContent className="pb-0">
        <ul className="divide-y divide-border">
          {group.types.map(type => (
            <TypeRow key={type._id} type={type} />
          ))}
        </ul>
      </AccordionContent>
    </AccordionItem>
  );
}

export function InsuranceTypesListPage() {
  const navigate = useNavigate();
  const { groups, loading, hasOrphans } = useInsuranceTypeList();

  if (loading) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex items-center justify-between mb-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-9 w-36" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-14 w-full rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  const defaultOpenValues = groups.map(g => g.providerName);

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-foreground" aria-hidden="true" />
          <h1 className="text-2xl font-semibold text-foreground">Insurance Types</h1>
        </div>
        <Button onClick={() => navigate('/insurance/types/new')}>
          <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
          Create new type
        </Button>
      </div>

      {/* Orphan banner */}
      {hasOrphans && (
        <div
          className={cn(
            'flex items-center gap-3 px-4 py-3 mb-6 rounded-md',
            'border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/30 dark:border-yellow-700'
          )}
        >
          <AlertTriangle
            className="w-5 h-5 shrink-0 text-yellow-700 dark:text-yellow-400"
            aria-hidden="true"
          />
          <p className="flex-1 text-sm font-medium text-yellow-900 dark:text-yellow-200">
            Some types are not yet linked to a provider.
          </p>
          <Button
            size="sm"
            className="shrink-0 bg-yellow-600 text-white hover:bg-yellow-700 dark:bg-yellow-700 dark:hover:bg-yellow-600"
            asChild
          >
            <Link to="/insurance/types/migration">Review</Link>
          </Button>
        </div>
      )}

      {/* Empty state */}
      {groups.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Shield className="w-12 h-12 text-muted-foreground mb-4" aria-hidden="true" />
          <h3 className="text-lg font-medium text-foreground mb-1">No insurance types yet</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Create your first insurance type to get started.
          </p>
          <Button onClick={() => navigate('/insurance/types/new')}>
            <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
            Create new type
          </Button>
        </div>
      )}

      {/* Accordion */}
      {groups.length > 0 && (
        <Accordion type="multiple" defaultValue={defaultOpenValues}>
          {groups.map(group => (
            <GroupItem key={group.providerName} group={group} />
          ))}
        </Accordion>
      )}
    </div>
  );
}
